import './styles/Root.scss';

import { Loading } from '@whistler-ui/k2/components/Loading';
import { Paragraph } from '@whistler-ui/k2/components/Typography';
import {
  commonPageStylesIdentifier,
  COOKIE_BANNER,
} from '@whistler-ui/k2/constants';
import { useClassHelper } from '@whistler-ui/k2/hooks';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Fragment, useState } from 'react';
import CookieConsent from 'react-cookie-consent';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { useMount } from 'react-use';

import { sessionTracker } from '../helpers/sessionTracker';
import {
  useAsyncGetOrganisationDetailsById,
  useGetCurrentUser,
} from '../hooks';
import { useRenderIf } from '../hooks/useRenderIf';
import { signOut } from '../services';
import { AuthService } from '../services/authService';
import { getCognitoUser } from '../services/cognito';
import {
  readCognitoTokenStorage,
  seedCognitoTokenStorage,
} from '../services/cognitoTokenSeed';
import { useAuthStore } from '../store/auth';
import { useThemeStore } from '../store/theme';
import { routes } from '.';
import { isInvalidOrganisation } from './helpers';

const componentName = 'c-Root';
export const inAuthWidget = window.self !== window.top;

const Root = () => {
  const { config } = useThemeStore();
  const { t } = useTranslation();
  const c = useClassHelper(componentName);
  const commonC = useClassHelper(commonPageStylesIdentifier);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [isLoading, setLoading] = useState(true);
  const { isGSMA } = useRenderIf();

  const { mutateAsync: getCurrentUser } = useGetCurrentUser();
  const { mutateAsync: getOrganisationDetails } =
    useAsyncGetOrganisationDetailsById();

  const { authValues, setAuthValues } = useAuthStore();

  const {
    cognito: cognitoUser,
    loggedUser,
    organisationState: { organisation },
  } = authValues;

  const shouldRenderCookieConsent = !isGSMA;

  const reviewPath = pathname.includes(routes.underReview.url);

  const initSession = async () => {
    try {
      let cognitoUser = null;
      const hash = window.location.hash;
      // Extract auth token
      const authDataMatch = hash.includes('auth_data=')
        ? hash.match(/#auth_data=(.*)/)
        : null;

      if (authDataMatch) {
        try {
          // 1. Seed v6's `cognitoUserPoolsTokenProvider` localStorage keys
          // from authWidget's raw hand-off tokens (undocumented v6 storage
          // contract — pinned by `cognitoTokenSeed.test.ts`).
          const tokens = JSON.parse(decodeURIComponent(authDataMatch[1]));
          const clientId = import.meta.env
            .REACT_APP_COGNITO_USER_POOL_WEB_CLIENT_ID;

          await seedCognitoTokenStorage(tokens, clientId);

          // 2. Hydrate Amplify's in-memory session from the seeded storage.
          await fetchAuthSession();

          // 3. Assigns the OUTER `cognitoUser` (fixes a latent bug: the v5
          // code declared an inner `const cognitoUser`, shadowing this
          // variable, so `authValues.cognito` stayed null even on a
          // successful hand-off).
          cognitoUser = await getCognitoUser({ silent: false });
        } catch (error) {
          // ???
          console.log(error.message);
        } finally {
          // 4. CRITICAL: Clear the hash from the URL immediately after use
          window.history.replaceState(
            '',
            document.title,
            window.location.pathname + window.location.search,
          );
        }
      } else {
        cognitoUser = await getCognitoUser({ silent: false });

        if (inAuthWidget) {
          await AuthService.signOut({ global: false });
        }
      }
      const loggedUser = await getCurrentUser({ silent: false });
      const organisationResult = await getOrganisationDetails({
        organisation_id: loggedUser.organisation_id,
        silent: true,
      });

      const authenticatedValues = {
        cognito: cognitoUser,
        loggedUser,
        organisationState: {
          organisation: organisationResult,
        },
      };

      setAuthValues(authenticatedValues);

      sessionTracker.start();

      const orgStatus = organisationResult?.status;

      const inactiveStatus = isInvalidOrganisation(orgStatus);

      if (inactiveStatus) {
        navigate(routes.underReview.url, { replace: true });
        return;
      }

      if (pathname === routes.basePath.url) {
        navigate(routes.dashboard.url, { replace: true });
      } else if (!inactiveStatus && reviewPath) {
        navigate(routes.dashboard.url);
      }
    } catch (error) {
      await signOut({ forceSignOut: true });

      if (pathname === routes.basePath.url) {
        return inAuthWidget
          ? navigate(routes.login.url, { replace: true })
          : navigate(routes.signup.url, { replace: true });
      }
    } finally {
      setLoading(false);
    }
  };

  useMount(() => {
    if (!cognitoUser || !loggedUser || !organisation) {
      initSession();
    }
  });

  if (isLoading) {
    return (
      <div className={c('__loading')}>
        <Loading />
      </div>
    );
  }

  return (
    <Fragment>
      <Outlet />
      {!inAuthWidget && shouldRenderCookieConsent && (
        <CookieConsent
          location="bottom"
          buttonText="Accept"
          declineButtonText="Decline"
          cookieName={COOKIE_BANNER}
          enableDeclineButton
          disableStyles
          containerClasses={commonC('__cookies-consent-container')}
          buttonWrapperClasses={commonC('__button-wrapper')}
          declineButtonClasses="p-Button p-Button--lg h-10 p-Button--secondary"
          buttonClasses="p-Button p-Button--lg h-10 p-Button--primary"
          expires={150}
          disableButtonStyles
        >
          <Paragraph>
            {t('cookies_consent')}
            {` `}
            <span>
              {t('cookies_consent_link')}{' '}
              <a
                href={config.environments.settings.cookiesPolicyURL}
                target="_blank"
                rel="noreferrer"
              >
                {t('cookies_consent_link_text')}
              </a>
            </span>
          </Paragraph>
          <Paragraph>{t('cookies_consent_end')}</Paragraph>
        </CookieConsent>
      )}
    </Fragment>
  );
};

/**
 * This function is designed to run inside an authentication iFrame.
 * Its purpose is to securely transfer the user's session tokens to the parent application
 * (the Portal) by leveraging the URL hash fragment (#) during a full page redirect.
 * * @param {string} username The username of the successfully authenticated Cognito user.
 * @returns {Promise<void>}
 */
export async function widgetAuthentication(usernameValue = '') {
  let username = usernameValue;
  if (!username) {
    const cognitoUser = await getCognitoUser({ silent: false });
    username = cognitoUser.username;
  }

  // v6's public `fetchAuthSession()` API never exposes the raw refresh
  // token to application code (confirmed against
  // `TokenOrchestrator.getTokens()` — it returns only
  // `{accessToken, idToken, signInDetails}`), so the tokens are read back
  // directly from the same seeded storage instead (see cognitoTokenSeed.ts).
  const clientId = import.meta.env.REACT_APP_COGNITO_USER_POOL_WEB_CLIENT_ID;
  const tokens = await readCognitoTokenStorage(clientId);

  // Package all necessary tokens and user data into a JSON string.
  const tokenPackage = JSON.stringify({
    idToken: tokens?.idToken,
    accessToken: tokens?.accessToken,
    refreshToken: tokens?.refreshToken,
    username,
  });

  // Encode the token package to ensure special characters are safe for the URL.
  const encodedTokenPackage = encodeURIComponent(tokenPackage);

  // The hash is used because its contents are not sent to the server, protecting the JWTs.
  const redirectHash = `#auth_data=${encodedTokenPackage}`;

  // CRITICAL STEP: Redirect the top-level window

  // 1. Get the full URL of the parent container hosting this iframe
  const parentUrl = document.referrer;
  let containerOrigin = '';

  if (parentUrl) {
    const urlObj = new URL(parentUrl);
    containerOrigin = urlObj.origin;
  }

  window.top.location.replace(containerOrigin + redirectHash);
}

export default Root;
